import { logger } from '@quan/logger';

import { Commonv2Services, Commonv2_AsyncTaskState } from '@quan/proto-api-client';

export interface GetAsyncTaskResultRsp<T = commonv2.GetAsyncTaskStateRsp> extends Promise<T> {
  /**
   * 停止轮询
   */
  stop: () => void;
}

/**
 * 获取异步任务状态
 * @param taskKey
 * @param maxTime 等待时间
 *        @default:5min
 * @param duration 轮询间隔
 *        @default:500ms
 */
export function getAsyncTaskResult(
  taskKey: string,
  maxTime: number = 5 * 60 * 1000,
  duration = 500,
): GetAsyncTaskResultRsp {
  const func = async (): Promise<commonv2.GetAsyncTaskStateRsp> => {
    if (!taskKey) return Promise.reject(new Error('taskKey is required'));
    try {
      const resp = await Commonv2Services.GetAsyncTaskState(
        {
          task_key: taskKey,
        },
        {
          skipFrequencyLimit: true,
        },
      );
      const { state } = resp;
      if (state === Commonv2_AsyncTaskState.AsyncTaskStateSuccess) {
        return resp!;
      }
      return Promise.reject(resp);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const endTime = new Date().valueOf() + maxTime;
  let timerId = 0;

  const runner = (): Promise<commonv2.GetAsyncTaskStateRsp> =>
    new Promise((resolve, reject) => {
      timerId = setTimeout(async () => {
        if (new Date().valueOf() > endTime) {
          logger.error({
            name: `getAsyncTaskResult timed out ${maxTime}ms task_key:[${taskKey}]`,
            message: '',
          });

          // 前端取消
          // eslint-disable-next-line prefer-promise-reject-errors
          return reject({
            state: Commonv2_AsyncTaskState.AsyncTaskStateFailure,
            error: {
              err_code: -1,
              err_msg: '执行超时，客户端取消',
            },
          } as commonv2.GetAsyncTaskStateRsp);
        }
        try {
          resolve(await func());
        } catch (error: any) {
          if (error && error.state === Commonv2_AsyncTaskState.AsyncTaskStateInProgress) {
            if (timerId) {
              resolve(runner());
            } else {
              reject(new Error('Manual stop'));
            }
          } else {
            logger.error({
              name: `getAsyncTaskResult error task_key:[${taskKey}]`,
              message: JSON.stringify(error),
            });
            reject(error);
          }
        }
      }, duration) as unknown as number;
    });

  const result = runner() as GetAsyncTaskResultRsp;

  result.stop = () => {
    if (timerId) {
      clearTimeout(timerId);
      timerId = 0;
    }
  };

  return result;
}

/**
 * 获取后端异步导出链接 在一段时间按照一定间隔轮询查询导出 url
 * @param taskKey
 * @param maxTime 等待时间
 *        @default:5min
 * @param duration 轮询间隔
 *        @default:500ms
 */
export function getExportFileUri(
  taskKey: string,
  maxTime?: number,
  duration?: number,
): GetAsyncTaskResultRsp<string> {
  const rsp = getAsyncTaskResult(taskKey, maxTime, duration);
  const result = rsp.then(({ url }) => {
    if (!url) {
      logger.error({
        name: `getExportFileUri task_key:[${taskKey}] url is empty`,
        message: '',
      });
      return Promise.reject(new Error('Url is empty'));
    }
    return url;
  }) as GetAsyncTaskResultRsp<string>;
  result.stop = rsp.stop;
  return result;
}
